
/**
 * Gera o HTML para o Relatório Individual de um Ativo (Ficha Técnica A4)
 */
export const generateIndividualAssetReportHtml = (d, liveData) => {
    const isOnline = (() => {
        if (!d?.lastSeen) return false;
        const lastSeenDate = new Date(d.lastSeen.seconds * 1000);
        const now = new Date();
        const diffInMinutes = (now - lastSeenDate) / 1000 / 60;
        return diffInMinutes < 5;
    })();

    const live = liveData?.[d.id] || {};
    
    // Cálculo do Score de Segurança
    let score = 0;
    if (d.security?.antivirus?.active) score += 25;
    if (d.security?.firewall?.state === 'Ativado') score += 25;
    if (d.security?.bitlocker?.encrypted) score += 25;
    if (d.security?.uac?.enabled) score += 25;
    const scoreColor = score >= 75 ? '#10b981' : (score >= 50 ? '#f59e0b' : '#ef4444');

    const disksHtml = (d.disks || []).map(disk => {
        const total = parseFloat(disk.totalSpaceGB || disk.total_gb || 0);
        const free = parseFloat(disk.freeSpaceGB || disk.free_gb || 0);
        const used = total - free;
        const percentUsed = total > 0 ? (used / total) * 100 : 0;
        const barColor = (free / total * 100) < 15 ? '#ef4444' : '#06b6d4';

        return `
            <div class="disk-item">
                <div class="disk-info">
                    <strong>Disco ${disk.drive}</strong>
                    <span>${used.toFixed(1)} GB / ${total.toFixed(1)} GB (${(total - used).toFixed(1)} GB livres)</span>
                </div>
                <div class="progress-bg">
                    <div class="progress-fill" style="width: ${percentUsed}%; background: ${barColor};"></div>
                </div>
            </div>
        `;
    }).join('');

    const gpus = live.gpus || d.graphics?.controllers || [];
    const gpusHtml = gpus.map((gpu, idx) => `
        <div class="spec-item">
            <span class="label">Placa de Vídeo ${idx + 1}</span>
            <span class="value">${gpu.name || gpu.model || 'N/A'}</span>
        </div>
    `).join('');

    return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <title>Ficha Técnica - ${d.hostname || 'Ativo'}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: 'Inter', -apple-system, sans-serif; 
            background: #f8fafc; 
            color: #1e293b; 
            line-height: 1.5;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        .page {
            width: 210mm;
            min-height: 297mm;
            padding: 20mm;
            margin: 10mm auto;
            background: white;
            box-shadow: 0 10px 25px rgba(0,0,0,0.05);
            position: relative;
        }

        @media print {
            @page { 
                size: A4;
                margin: 0; 
            }
            body { 
                background: white; 
                margin: 0 !important; 
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
            }
            .page { 
                margin: 0 !important; 
                box-shadow: none !important; 
                width: 210mm; 
                height: 297mm;
                padding: 15mm !important; /* Reduzi um pouco o padding para dar mais margem de segurança */
                overflow: hidden;
            }
        }

        /* Header */
        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 4px solid #06b6d4;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }

        .logo-box {
            display: flex;
            flex-direction: column;
        }

        .logo-text {
            font-size: 24px;
            font-weight: 800;
            color: #0f172a;
            letter-spacing: -0.5px;
        }

        .logo-text span { color: #06b6d4; }

        .report-title {
            text-align: right;
        }

        .report-title h1 {
            font-size: 18px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 4px;
        }

        .report-title p {
            font-size: 12px;
            color: #94a3b8;
        }

        /* Layout Grid */
        .grid {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 25px;
        }

        .section-title {
            font-size: 11px;
            font-weight: 800;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .section-title::after {
            content: '';
            flex: 1;
            height: 1px;
            background: #e2e8f0;
        }

        /* Info Cards */
        .card {
            background: #f8fafc;
            border: 1px solid #f1f5f9;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 25px;
        }

        .asset-hero {
            display: flex;
            gap: 20px;
            align-items: center;
            margin-bottom: 30px;
        }

        .asset-icon {
            width: 60px;
            height: 60px;
            background: #0f172a;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #06b6d4;
        }

        .asset-main-info h2 {
            font-size: 24px;
            font-weight: 800;
            color: #0f172a;
        }

        .status-badge {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font-size: 10px;
            font-weight: 700;
            padding: 3px 8px;
            border-radius: 20px;
            text-transform: uppercase;
        }

        .status-online { background: #ecfdf5; color: #10b981; }
        .status-offline { background: #f1f5f9; color: #64748b; }

        /* Spec Lists */
        .spec-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
        }

        .spec-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
        .value { font-size: 13px; font-weight: 600; color: #334155; }

        /* Disks */
        .disk-item { margin-bottom: 12px; }
        .disk-info { display: flex; justify-content: space-between; font-size: 12px; margin-bottom: 5px; }
        .progress-bg { height: 6px; background: #e2e8f0; border-radius: 10px; overflow: hidden; }
        .progress-fill { height: 100%; border-radius: 10px; }

        /* Security Score */
        .score-card {
            background: #0f172a;
            color: white;
            border-radius: 16px;
            padding: 25px;
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            box-shadow: 0 10px 20px rgba(15, 23, 42, 0.15);
        }

        .score-value {
            font-size: 42px;
            font-weight: 800;
            line-height: 1;
            margin: 10px 0;
        }

        .score-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; }

        .security-list {
            margin-top: 20px;
            width: 100%;
            text-align: left;
        }

        .sec-item {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            padding: 8px 0;
            border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .sec-item:last-child { border: none; }

        .badge {
            padding: 2px 6px;
            border-radius: 4px;
            font-weight: 700;
            font-size: 9px;
            text-transform: uppercase;
        }

        .bg-success { background: #10b981; color: white; }
        .bg-danger { background: #ef4444; color: white; }
        .bg-warning { background: #f59e0b; color: white; }

        /* Footer */
        .footer {
            position: absolute;
            bottom: 20mm;
            left: 20mm;
            right: 20mm;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: #94a3b8;
        }
    </style>
</head>
<body>
    <div class="page">
        <header class="header">
            <div class="logo-box">
                <div class="logo-text">Arcus<span>.</span> Asset Management</div>
                <div style="font-size: 10px; color: #94a3b8; font-weight: 600; margin-top: 2px;">TECNOLOGIA EM GESTÃO DE TI</div>
            </div>
            <div class="report-title">
                <h1>Ficha Técnica de Ativo</h1>
                <p>Relatório gerado em ${new Date().toLocaleString('pt-BR')}</p>
            </div>
        </header>

        <div class="asset-hero">
            <div class="asset-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
            </div>
            <div class="asset-main-info">
                <h2>${d.hostname || 'Hostname N/A'}</h2>
                <div class="status-badge ${isOnline ? 'status-online' : 'status-offline'}">
                    <span style="width: 6px; height: 6px; border-radius: 50%; background: currentColor;"></span>
                    ${isOnline ? 'Conectado / Online' : 'Desconectado / Offline'}
                </div>
            </div>
        </div>

        <div class="grid">
            <div class="left-col">
                <div class="section-title">Informações do Sistema</div>
                <div class="card">
                    <div class="spec-grid">
                        <div class="spec-item"><span class="label">Sistema Operacional</span><span class="value">${d.os?.distro || d.os_version || 'N/A'}</span></div>
                        <div class="spec-item"><span class="label">Versão / Build</span><span class="value">${d.os?.release || 'N/A'} ${d.os?.build ? `(Build ${d.os.build})` : ''}</span></div>
                        <div class="spec-item"><span class="label">Usuário Principal</span><span class="value">${d.username || 'N/A'}</span></div>
                        <div class="spec-item"><span class="label">Etiqueta Arcus</span><span class="value">${d.etiqueta || 'N/A'}</span></div>
                        <div class="spec-item"><span class="label">Endereço IP Local</span><span class="value">${d.localIp || d.network?.[0]?.ip || 'N/A'}</span></div>
                        <div class="spec-item"><span class="label">Endereço IP Público</span><span class="value">${d.public_ip || 'N/A'}</span></div>
                    </div>
                </div>

                <div class="section-title">Especificações de Hardware</div>
                <div class="card">
                    <div class="spec-item" style="margin-bottom: 15px;">
                        <span class="label">Processador (CPU)</span>
                        <span class="value">${typeof d.cpu === 'object' ? d.cpu.brand : (d.cpu || 'N/A')}</span>
                        <span style="font-size: 11px; color: #94a3b8;">${d.cpu?.cores || 'N/A'} Cores / ${d.cpu?.threads || 'N/A'} Threads | ${d.cpu?.speed || ''}</span>
                    </div>
                    <div class="spec-item" style="margin-bottom: 15px;">
                        <span class="label">Memória RAM</span>
                        <span class="value">${d.ram?.totalGB || d.ram_total_gb || 'N/A'} GB Total</span>
                    </div>
                    ${gpusHtml}
                </div>

                <div class="section-title">Armazenamento</div>
                <div class="card">
                    ${disksHtml || '<p style="font-size: 12px; color: #94a3b8;">Nenhum disco detectado.</p>'}
                </div>
            </div>

            <div class="right-col">
                <div class="section-title">Segurança</div>
                <div class="score-card">
                    <div class="score-label">Maturidade</div>
                    <div class="score-value" style="color: ${scoreColor}">${score}%</div>
                    <div style="font-size: 10px; font-weight: 600; color: #64748b;">Nível de Conformidade</div>
                    
                    <div class="security-list">
                        <div class="sec-item">
                            <span>Antivírus</span>
                            <span class="badge ${d.security?.antivirus?.active ? 'bg-success' : 'bg-danger'}">${d.security?.antivirus?.active ? 'OK' : 'ERRO'}</span>
                        </div>
                        <div class="sec-item">
                            <span>Firewall</span>
                            <span class="badge ${d.security?.firewall?.state === 'Ativado' ? 'bg-success' : 'bg-danger'}">${d.security?.firewall?.state === 'Ativado' ? 'ATIVO' : 'OFF'}</span>
                        </div>
                        <div class="sec-item">
                            <span>BitLocker</span>
                            <span class="badge ${d.security?.bitlocker?.encrypted ? 'bg-success' : 'bg-warning'}">${d.security?.bitlocker?.encrypted ? 'ATIVO' : 'OFF'}</span>
                        </div>
                        <div class="sec-item">
                            <span>UAC (Controle de Conta)</span>
                            <span class="badge ${d.security?.uac?.enabled ? 'bg-success' : 'bg-danger'}">${d.security?.uac?.enabled ? 'ATIVO' : 'OFF'}</span>
                        </div>
                    </div>
                </div>

                <div class="section-title" style="margin-top: 30px;">Licenciamento</div>
                <div class="card" style="padding: 15px;">
                    <div class="sec-item" style="color: #334155;">
                        <span style="font-weight: 600;">Windows</span>
                        <span class="badge ${d.license?.windows?.statusCode === 1 ? 'bg-success' : 'bg-danger'}">${d.license?.windows?.status || 'N/A'}</span>
                    </div>
                    <div class="sec-item" style="color: #334155; border: none;">
                        <span style="font-weight: 600;">Office</span>
                        <span class="badge ${d.license?.office?.installed ? (d.license.office.status === 'Ativado' ? 'bg-success' : 'bg-warning') : 'bg-warning'}">${d.license?.office?.status || 'N/A'}</span>
                    </div>
                </div>

                <div class="section-title" style="margin-top: 30px;">Agente</div>
                <div class="card" style="padding: 15px; text-align: center;">
                    <div style="font-size: 13px; font-weight: 700; color: #0f172a;">Versão ${d.agent_version || '1.0'}</div>
                    <div style="font-size: 10px; color: #94a3b8; margin-top: 4px;">Monitoramento Ativo</div>
                </div>
            </div>
        </div>

        <footer class="footer">
            <div>ID do Ativo: ${d.id || 'N/A'}</div>
            <div>Documento Oficial Arcus - Uso Interno e Confidencial</div>
            <div>Página 1 de 1</div>
        </footer>
    </div>
</body>
</html>
    `;
};
